import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Camera, Ruler, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { Logo } from "../components/Logo";

const HERO = "https://static.prod-images.emergentagent.com/jobs/166ceceb-055c-4119-89c1-400addb91182/images/bd72ef1fc5de86ea5cdccc55609d72ee09118cbe851ec43fa6c50ae5ca90e2ed.jpeg";
const WOMEN = "https://images.unsplash.com/photo-1642472169780-6ff9b74a3058";
const MEN = "https://images.unsplash.com/photo-1710488140847-c076d4e80cc8";
const OCEAN = "https://images.unsplash.com/photo-1564955731632-aae2940730ec";
const GALLERY = [
  "https://images.unsplash.com/photo-1536767421132-638166620e78",
  "https://images.unsplash.com/photo-1588710355352-1d706fe8e14f",
  "https://images.unsplash.com/photo-1548047724-65d28c26d454",
  "https://images.unsplash.com/photo-1608775260802-613544549189",
];

const steps = [
  { icon: Camera, title: "Photograph", text: "Three phone photos in fitted underwear — front, side, back. Two minutes, at home." },
  { icon: Sparkles, title: "We read your body", text: "Our fit intelligence maps your exact proportions from the images you upload." },
  { icon: Ruler, title: "Tailored & sewn", text: "Each piece is cut and handsewn to your measurements. A fit that simply disappears." },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setFeatured(r.data.filter((p) => p.featured).slice(0, 6)));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO — single image: woman & man together */}
      <section className="relative h-screen min-h-[640px] w-full overflow-hidden">
        <img src={HERO} alt="Woman and man by the sea" className="absolute inset-0 w-full h-full object-cover object-center hero-zoom" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/15 to-black/60" />
        <div className="relative h-full max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col justify-end items-center text-center pb-20">
          <Logo tone="white" className="h-28 md:h-40 lg:h-48 w-auto mb-6 fade-up" style={{ animationDelay: "120ms" }} />
          <p className="text-white/90 text-lg md:text-xl max-w-lg mt-6 font-light fade-up" style={{ animationDelay: "240ms" }}>
            Salt Love. Swimwear cut to your exact body — for her and for him — from three photos taken on your phone.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-9 fade-up" style={{ animationDelay: "360ms" }}>
            <Link
              to="/women"
              data-testid="hero-women-cta"
              className="inline-flex items-center gap-3 bg-sand text-charcoal px-8 py-4 rounded-full overline hover:bg-white transition-colors"
            >
              Shop Women
            </Link>
            <Link
              to="/men"
              data-testid="hero-men-cta"
              className="inline-flex items-center gap-3 bg-sand text-charcoal px-8 py-4 rounded-full overline hover:bg-white transition-colors"
            >
              Shop Men
            </Link>
            <Link
              to="/fit-studio"
              data-testid="hero-fit-cta"
              className="group inline-flex items-center gap-3 border border-white/70 text-white px-8 py-4 rounded-full overline hover:bg-white/10 transition-colors"
            >
              Find your fit
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="max-w-[1000px] mx-auto px-6 lg:px-10 py-28 text-center">
        <div className="font-script text-4xl text-sunkissed mb-6">海を愛する</div>
        <h2 className="font-serif text-3xl md:text-5xl leading-tight font-light">
          Bikinis and swimsuits should fit like the water —
          <span className="italic"> effortlessly, and only to you.</span>
        </h2>
        <p className="text-charcoal/60 max-w-xl mx-auto mt-8 leading-relaxed">
          No more guessing between S, M and L. SHIOKOI reads your body from three photographs and
          crafts each piece to your precise measurements — for women and men.
        </p>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-6">
        {[
          { to: "/women", label: "Women", sub: "Bikinis · One-Pieces", img: WOMEN },
          { to: "/men", label: "Men", sub: "Tailored Trunks", img: MEN },
        ].map((c) => (
          <Link
            key={c.to}
            to={c.to}
            data-testid={`category-${c.label.toLowerCase()}`}
            className="group relative aspect-[4/5] md:aspect-[3/4] overflow-hidden rounded-3xl"
          >
            <img src={c.img} alt={c.label} className="w-full h-full object-cover transition-transform duration-[1000ms] group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />
            <div className="absolute bottom-0 left-0 p-10">
              <div className="overline text-white/70 mb-2">{c.sub}</div>
              <div className="flex items-center gap-4 text-white">
                <span className="font-serif text-5xl">{c.label}</span>
                <ArrowRight className="group-hover:translate-x-2 transition-transform" />
              </div>
            </div>
          </Link>
        ))}
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28">
        <div className="text-center mb-16">
          <div className="overline text-sunkissed mb-4">The Fit Studio</div>
          <h2 className="font-serif text-4xl md:text-5xl font-light">A perfect fit in three photos</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-12">
          {steps.map((s, i) => (
            <div key={s.title} data-testid={`how-step-${i}`} className="text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start mb-6">
                <div className="w-14 h-14 rounded-full border border-charcoal/20 flex items-center justify-center">
                  <s.icon size={22} strokeWidth={1.3} />
                </div>
                <span className="ml-4 font-serif text-3xl text-charcoal/25">0{i + 1}</span>
              </div>
              <h3 className="font-serif text-2xl mb-3">{s.title}</h3>
              <p className="text-charcoal/60 leading-relaxed text-sm">{s.text}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-14">
          <Link
            to="/fit-studio"
            data-testid="how-cta"
            className="inline-flex items-center gap-3 bg-ocean text-sand px-8 py-4 overline hover:opacity-90 transition-opacity"
          >
            Start your fit profile <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-12">
            <h2 className="font-serif text-3xl md:text-4xl font-light">The Signature Edit</h2>
            <Link to="/swimwear" className="overline hover:opacity-60 transition-opacity flex items-center gap-2">
              View all <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </section>
      )}

      {/* LIFESTYLE GALLERY */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-8">
        <div className="text-center mb-12">
          <div className="overline text-sunkissed mb-4">Lived in the sun</div>
          <h2 className="font-serif text-4xl md:text-5xl font-light">The SHIOKOI diary</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {GALLERY.map((src, i) => (
            <div
              key={i}
              data-testid={`gallery-${i}`}
              className={`relative overflow-hidden rounded-3xl bg-linen ${i % 4 === 0 || i % 4 === 3 ? "aspect-[3/4]" : "aspect-[3/4] md:mt-10"}`}
            >
              <img src={src} alt="SHIOKOI lifestyle" loading="lazy" className="w-full h-full object-cover transition-transform duration-[1200ms] hover:scale-105" />
            </div>
          ))}
        </div>
      </section>

      {/* OCEAN BAND */}
      <section className="relative h-[420px] mt-28 overflow-hidden flex items-center justify-center">
        <img src={OCEAN} alt="Ocean" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ocean/40" />
        <div className="relative text-center px-6">
          <div className="font-script text-5xl md:text-6xl text-white mb-4">For the love of salt.</div>
          <p className="text-white/80 overline">Tailored for sun kissed days and salty skin</p>
        </div>
      </section>
    </div>
  );
}
